// Decorates a model's diagram with what changed since an older model: new, changed and renamed
// cards and rows, plus ghosts of removed entities, properties, owned types and relationships.
import { changeEntity, propertyChangeNote, rootOfPath, type ModelDiff, type ModelSpec, type RelationshipSpec } from '@modelviewer/core';
import { buildGraph, sizeNode, type DiffState, type EntityNodeData, type Graph, type GraphEdge, type GraphOptions, type Line } from '../diagram/graph';

export interface DiffGraphInput {
  before: ModelSpec;
  after: ModelSpec;
  diff: ModelDiff;
  options: GraphOptions;
  /** Node id for an entity of the newer model (defaults to its key). */
  idAfter?: (key: string) => string;
  /** Node id for an entity of the older model (defaults to the id of what it became). */
  idBefore?: (key: string) => string;
}

const lineId = (l: Line) => (l.kind === 'row' ? `r:${l.row.id}` : `s:${l.section.id}`);
const lineName = (l: Line) => (l.kind === 'row' ? l.row.id : l.section.id);

function ghost(l: Line): Line {
  return l.kind === 'row' ? { kind: 'row', row: { ...l.row, diff: 'removed' } } : { kind: 'section', section: { ...l.section, diff: 'removed' } };
}

/** Inserts removed lines of the older card into the newer one, each after the line it followed. */
function insertGhosts(node: EntityNodeData, before: EntityNodeData, removed: (l: Line) => boolean): void {
  const out = [...node.lines];
  let at = 0;
  for (const bl of before.lines) {
    if (removed(bl)) {
      out.splice(at, 0, ghost(bl));
      at++;
      continue;
    }
    const id = lineId(bl);
    const found = out.findIndex((l) => lineId(l) === id && l.kind === bl.kind && (l.kind === 'row' ? l.row.diff : l.section.diff) !== 'removed');
    if (found >= 0) at = found + 1;
  }
  node.lines = out;
}

function mark(node: EntityNodeData | undefined, state: DiffState): void {
  if (node && node.diff !== 'added') node.diff = state;
}

export function buildDiffGraph(input: DiffGraphInput): Graph {
  const { before, after, diff, options } = input;
  const idAfter = input.idAfter ?? ((k: string) => k);
  const toAfter = new Map(diff.entities.map((m) => [m.from, m.to]));
  const idBefore = input.idBefore ?? ((k: string) => idAfter(toAfter.get(k) ?? k));
  const fromBefore = new Map(diff.entities.map((m) => [m.to, m.from]));

  const graph = buildGraph(after, options);
  const old = buildGraph(before, options);
  const nodes = new Map(graph.nodes.map((n) => [n.key, n]));
  const oldNodes = new Map(old.nodes.map((n) => [n.key, n]));
  for (const n of graph.nodes) {
    const previous = fromBefore.get(n.key);
    if (previous !== undefined && previous !== n.key) n.previousKey = previous;
  }

  // Rows and sections, by card and line id.
  const rowStates = new Map<string, Map<string, { state: DiffState; note?: string }>>();
  const setLine = (path: string, name: string, state: DiffState, note?: string) => {
    const { root, rest } = rootOfPath(after, path);
    const map = rowStates.get(root) ?? new Map<string, { state: DiffState; note?: string }>();
    map.set([...rest, name].join('.'), { state, note });
    rowStates.set(root, map);
  };
  /** Removed lines, per newer card key: a predicate over the older card's lines. */
  const removals = new Map<string, ((l: Line) => boolean)[]>();
  const remove = (path: string, test: (rest: string[]) => (l: Line) => boolean) => {
    const { root, rest } = rootOfPath(after, path);
    const list = removals.get(root) ?? [];
    list.push(test(rest));
    removals.set(root, list);
  };

  const addedRelationships = new Set<RelationshipSpec>();
  const changedRelationships = new Set<RelationshipSpec>();
  const removedRelationships = new Set<RelationshipSpec>();
  const addedJoins = new Set<string>();
  const removedJoins = new Set<string>();
  const removedEntities = new Set<string>();
  const baseChanged = new Set<string>();

  for (const c of diff.changes) {
    switch (c.kind) {
      case 'entity-added':
        mark(nodes.get(c.entity), 'added');
        continue;
      case 'entity-removed':
        removedEntities.add(c.entity);
        continue;
      case 'entity-renamed': {
        const n = nodes.get(c.to);
        mark(n, 'changed');
        if (n) n.note = `was ${c.from}`;
        continue;
      }
      case 'many-to-many-added':
        addedJoins.add(c.join);
        mark(nodes.get(c.join), 'added');
        continue;
      case 'many-to-many-removed':
        removedJoins.add(c.join);
        continue;
      case 'relationship-added':
        addedRelationships.add(c.relationship);
        continue;
      case 'relationship-removed':
        removedRelationships.add(c.relationship);
        continue;
      case 'relationship-changed':
        changedRelationships.add(c.after);
        continue;
      case 'property-added':
        setLine(c.entity, c.property, 'added');
        break;
      case 'property-renamed':
        setLine(c.entity, c.to, 'changed', `was ${c.from}`);
        break;
      case 'property-changed':
        setLine(c.entity, c.property, 'changed', propertyChangeNote(c.before, c.after, c.facets));
        break;
      case 'property-removed':
        remove(c.entity, (rest) => {
          const id = [...rest, c.property].join('.');
          return (l) => l.kind === 'row' && l.row.id === id;
        });
        break;
      case 'owned-added':
        setLine(c.entity, c.navigation, 'added');
        break;
      case 'complex-added':
        setLine(c.entity, c.property, 'added');
        break;
      case 'owned-changed':
        setLine(c.entity, c.navigation, 'changed');
        break;
      case 'complex-changed':
        setLine(c.entity, c.property, 'changed');
        break;
      case 'owned-removed':
      case 'complex-removed': {
        const name = 'navigation' in c ? c.navigation : c.property;
        remove(c.entity, (rest) => {
          const id = [...rest, name].join('.');
          return (l) => lineName(l) === id || lineName(l).startsWith(`${id}.`);
        });
        break;
      }
      case 'inheritance-changed':
        if (c.facets.includes('base')) baseChanged.add(c.entity);
        break;
      default:
        break;
    }
    const path = changeEntity(c);
    if (path) mark(nodes.get(rootOfPath(after, path).root), 'changed');
  }

  // Row states; lines under a new or changed section share its state when new.
  for (const [key, states] of rowStates) {
    const n = nodes.get(key);
    if (!n) continue;
    const addedSections = [...states].filter(([, s]) => s.state === 'added').map(([id]) => id);
    for (const l of n.lines) {
      const id = lineName(l);
      const s = states.get(id) ?? (addedSections.some((p) => id.startsWith(`${p}.`)) ? { state: 'added' as const } : undefined);
      if (!s) continue;
      if (l.kind === 'row') {
        l.row.diff = s.state;
        if (s.note) l.row.note = s.note;
      } else {
        l.section.diff = s.state;
      }
    }
  }
  for (const [key, tests] of removals) {
    const n = nodes.get(key);
    const was = n ? oldNodes.get(fromBefore.get(key) ?? key) : undefined;
    if (n && was) insertGhosts(n, was, (l) => tests.some((t) => t(l)));
  }

  // Ghost cards for removed entities (and join tables drawn as cards).
  const ghosts: EntityNodeData[] = [];
  for (const key of [...removedEntities, ...removedJoins]) {
    const was = oldNodes.get(key);
    if (was) ghosts.push({ ...was, id: idBefore(key), diff: 'removed' });
  }

  // Edge states, and ghosts of removed relationships.
  const joinOf = (e: GraphEdge) => (e.kind === 'manyToMany' ? e.id.slice('m2m:'.length) : null);
  for (const e of graph.edges) {
    if (e.kind === 'relationship' && e.relationship !== null) {
      const r = after.relationships[e.relationship]!;
      if (addedRelationships.has(r)) e.diff = 'added';
      else if (changedRelationships.has(r)) e.diff = 'changed';
    } else if (e.kind === 'manyToMany') {
      if (addedJoins.has(joinOf(e)!)) e.diff = 'added';
    } else if (e.kind === 'inheritance') {
      if (nodes.get(e.source)?.diff === 'added' || baseChanged.has(e.source)) e.diff = 'added';
    }
  }
  const ghostEdges: GraphEdge[] = [];
  for (const e of old.edges) {
    let gone = false;
    if (e.kind === 'relationship' && e.relationship !== null) gone = removedRelationships.has(before.relationships[e.relationship]!);
    else if (e.kind === 'manyToMany') gone = removedJoins.has(joinOf(e)!);
    else if (e.kind === 'inheritance') gone = removedEntities.has(e.source) || baseChanged.has(toAfter.get(e.source) ?? e.source);
    if (gone) ghostEdges.push({ ...e, id: `was:${e.id}`, source: e.source, target: e.target, diff: 'removed' });
  }

  // Final ids, then anchors for the rows that edges attach to.
  for (const n of graph.nodes) n.id = idAfter(n.key);
  for (const e of graph.edges) {
    e.source = idAfter(e.source);
    e.target = idAfter(e.target);
  }
  for (const e of ghostEdges) {
    e.source = idBefore(e.source);
    e.target = idBefore(e.target);
  }
  const all = [...graph.nodes, ...ghosts];
  const byId = new Map(all.map((n) => [n.id, n]));
  const hasRow = (n: EntityNodeData, row: string | null) => row !== null && n.lines.some((l) => l.kind === 'row' && l.row.id === row);
  const edges = [...graph.edges, ...ghostEdges.filter((e) => byId.has(e.source) && byId.has(e.target))];
  for (const n of all) n.anchors = [];
  for (const e of edges) {
    const s = byId.get(e.source)!;
    const t = byId.get(e.target)!;
    if (!hasRow(s, e.sourceRow)) e.sourceRow = null;
    if (!hasRow(t, e.targetRow)) e.targetRow = null;
    if (e.sourceRow) s.anchors.push(e.sourceRow);
    if (e.targetRow) t.anchors.push(e.targetRow);
  }
  const measure = options.measure;
  const sized = all.map((n) => sizeNode({ ...n, anchors: [...new Set(n.anchors)] }, measure));
  return { nodes: sized, edges };
}
